import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bus, ClipboardCheck, Pencil, Plus, UsersRound } from 'lucide-react';
import { ROLES } from '../config/accessControl';
import {
  VEHICLE_STATUS_META,
  VEHICLE_STATUSES,
} from '../config/vehicleStatus';
import { useAuth } from '../context/AuthContext';
import { vehicleService } from '../services';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import ImageUpload from '../components/forms/ImageUpload';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Table from '../components/tables/Table';

const vehicleTypeOptions = ['Automóvil', 'Camioneta', 'Van', 'Minibús', 'Bus', 'Motocicleta']
  .map((value) => ({ value, label: value }));
const statusOptions = Object.entries(VEHICLE_STATUS_META)
  .map(([value, meta]) => ({ value, label: meta.label }));
const initialForm = {
  coordinator_id: '',
  driver_id: '',
  vehicle_type: 'Van',
  brand: '',
  model: '',
  year: String(new Date().getFullYear()),
  color: '',
  license_plate: '',
  capacity: '',
  status: VEHICLE_STATUSES.AVAILABLE,
  zone: '',
  observations: '',
};

function VehiclesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [options, setOptions] = useState({ coordinators: [], drivers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [modalMode, setModalMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [documents, setDocuments] = useState({
    authorization_image: null,
    green_card_front_image: null,
    green_card_back_image: null,
  });
  const [saving, setSaving] = useState(false);

  const canCreate = [
    ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL, ROLES.COORDINADOR,
  ].includes(user.role);
  const canReview = [ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL].includes(user.role);
  const canEdit = (vehicle) =>
    user.role === ROLES.ADMINISTRADOR ||
    (user.role === ROLES.ADMINISTRADOR_GENERAL) ||
    (user.role === ROLES.COORDINADOR && vehicle.coordinator_id === user.relaciones?.coordinador_id);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await vehicleService.getForActor(user);
      setVehicles(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const loadOptions = async (coordinatorId) => {
    try {
      const response = await vehicleService.getFormOptions(user, coordinatorId);
      setOptions(response.data);
    } catch (requestError) {
      setError(requestError.message);
      setOptions({ coordinators: [], drivers: [] });
    }
  };

  const openCreate = async () => {
    const coordinatorId = user.role === ROLES.COORDINADOR ? user.relaciones?.coordinador_id : '';
    setEditing(null);
    setForm({ ...initialForm, coordinator_id: coordinatorId });
    setDocuments({ authorization_image: null, green_card_front_image: null, green_card_back_image: null });
    setOptions({ coordinators: [], drivers: [] });
    setError('');
    setModalMode('create');
    if (coordinatorId) await loadOptions(coordinatorId);
    else await loadOptions('');
  };

  const openEdit = async (vehicle) => {
    setEditing(vehicle);
    setForm({
      coordinator_id: vehicle.coordinator_id,
      driver_id: vehicle.driver_id,
      vehicle_type: vehicle.vehicle_type,
      brand: vehicle.brand,
      model: vehicle.model,
      year: String(vehicle.year),
      color: vehicle.color,
      license_plate: vehicle.license_plate,
      capacity: String(vehicle.capacity),
      status: vehicle.status,
      zone: vehicle.zone,
      observations: vehicle.observations,
    });
    setDocuments({ authorization_image: null, green_card_front_image: null, green_card_back_image: null });
    setError('');
    setModalMode('edit');
    await loadOptions(vehicle.coordinator_id);
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const changeCoordinator = async (coordinatorId) => {
    setForm((current) => ({ ...current, coordinator_id: coordinatorId, driver_id: '' }));
    if (coordinatorId) await loadOptions(coordinatorId);
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, ...documents };
      if (modalMode === 'create') await vehicleService.createForActor(payload, user);
      else await vehicleService.updateForActor(editing.id, payload, user);
      setModalMode(null);
      setFeedback(modalMode === 'create' ? 'Móvil presentado y pendiente de aprobación.' : 'Móvil actualizado; deberá revisarse nuevamente.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const driverOptions = useMemo(() => {
    const result = options.drivers.filter((item) =>
      item.status !== 'ASSIGNED' || item.id === editing?.driver_id
    );
    if (editing?.driver && !result.some((item) => item.id === editing.driver_id)) {
      result.push({ id: editing.driver_id, name: editing.driverName, status: 'ASSIGNED' });
    }
    return result;
  }, [editing, options.drivers]);

  const formComplete = form.coordinator_id && form.driver_id &&
    form.vehicle_type && form.brand.trim() && form.model.trim() &&
    form.year && form.color.trim() && form.license_plate.trim() &&
    Number(form.capacity) > 0 && form.zone.trim() && (
      modalMode === 'edit' || (
        documents.authorization_image && documents.green_card_front_image
        && documents.green_card_back_image
      )
    );

  const columns = [
    {
      key: 'license_plate',
      header: 'Chapa',
      render: (value) => <span className="rounded bg-slate-900 px-2 py-1 font-mono text-xs font-semibold text-white">{value}</span>,
    },
    { key: 'vehicle_type', header: 'Tipo' },
    { key: 'brand', header: 'Vehículo', render: (_, item) => <div><p className="font-semibold text-content-primary">{item.brand} {item.model}</p><p className="text-xs text-content-muted">{item.year} · {item.color}</p></div> },
    { key: 'capacity', header: 'Capacidad', render: (value) => `${value} personas` },
    { key: 'driverName', header: 'Conductor principal' },
    { key: 'zone', header: 'Zona' },
    { key: 'approval_status', header: 'Revisión', render: (value) => <Badge variant={value === 'APPROVED' ? 'success' : value === 'REJECTED' ? 'danger' : 'warning'}>{value === 'APPROVED' ? 'Aprobado' : value === 'REJECTED' ? 'Rechazado' : 'Pendiente'}</Badge> },
    {
      key: 'status',
      header: 'Estado',
      render: (value) => {
        const meta = VEHICLE_STATUS_META[value] || { label: value, variant: 'neutral' };
        return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_, item) => (
        <div className="flex gap-2">
          {canReview ? (
            <Button variant="outline" size="sm" onClick={() => navigate(`/revision-flota?vehicle_id=${item.id}`)}>
              <ClipboardCheck className="size-4" /> Revisar
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/moviles/${item.id}`)}>Consultar</Button>
          )}
          {canEdit(item) && <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Pencil className="size-4" /> Editar</Button>}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-content-primary">Móviles</h2>
          <p className="mt-1 text-sm text-content-muted">Vehículos y conductores principales de la campaña activa.</p>
        </div>
        {canCreate && <Button onClick={openCreate}><Plus className="size-4" /> Agregar móvil</Button>}
      </div>

      {feedback && <Alert variant="success" title="Operación completada" className="mb-5">{feedback}</Alert>}
      {error && !modalMode && <Alert variant="danger" title="No se pudo completar" className="mb-5">{error}</Alert>}

      <div className="mb-5 grid gap-4 sm:grid-cols-2">
        <Card><div className="flex items-center gap-3"><span className="grid size-10 place-items-center rounded-control bg-primary-50 text-primary-700"><Bus className="size-5" /></span><div><p className="text-sm text-content-muted">Móviles visibles</p><p className="text-2xl font-semibold text-content-primary">{vehicles.length}</p></div></div></Card>
        <Card><div className="flex items-center gap-3"><span className="grid size-10 place-items-center rounded-control bg-success-50 text-success-700"><UsersRound className="size-5" /></span><div><p className="text-sm text-content-muted">Capacidad total</p><p className="text-2xl font-semibold text-content-primary">{vehicles.reduce((sum, item) => sum + item.capacity, 0)}</p></div></div></Card>
      </div>

      {loading ? <div className="grid min-h-64 place-items-center"><LoadingSpinner label="Cargando móviles" /></div> : vehicles.length ? <Table columns={columns} data={vehicles} /> : <EmptyState icon={Bus} title="Sin móviles" description="No hay móviles registrados para esta campaña y estructura." />}

      <Modal open={Boolean(modalMode)} onClose={() => !saving && setModalMode(null)} size="lg" title={modalMode === 'create' ? 'Agregar móvil' : 'Editar móvil'} description="El móvil debe conservar exactamente un conductor principal." footer={<><Button variant="ghost" onClick={() => setModalMode(null)}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!formComplete}>Guardar móvil</Button></>}>
        <div className="space-y-5">
          {error && <Alert variant="danger" title="No se pudo guardar">{error}</Alert>}
          {user.role !== ROLES.COORDINADOR ? (
            <Select id="vehicle-coordinator" label="Coordinador propietario" value={form.coordinator_id} onChange={(event) => changeCoordinator(event.target.value)} options={options.coordinators.map((item) => ({ value: item.id, label: item.name }))} placeholder="Seleccionar coordinador" required disabled={modalMode === 'edit'} />
          ) : (
            <Alert variant="info" title="Propietario de gestión">El móvil se asociará automáticamente a tu coordinación.</Alert>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Select id="vehicle-type" label="Tipo de vehículo" value={form.vehicle_type} onChange={(event) => setField('vehicle_type', event.target.value)} options={vehicleTypeOptions} required />
            <Input id="vehicle-brand" label="Marca" value={form.brand} onChange={(event) => setField('brand', event.target.value)} required />
            <Input id="vehicle-model" label="Modelo" value={form.model} onChange={(event) => setField('model', event.target.value)} required />
            <Input id="vehicle-year" type="number" min="1950" max={new Date().getFullYear() + 1} label="Año" value={form.year} onChange={(event) => setField('year', event.target.value)} required />
            <Input id="vehicle-color" label="Color" value={form.color} onChange={(event) => setField('color', event.target.value)} required />
            <Input id="vehicle-license-plate" label="Chapa" value={form.license_plate} onChange={(event) => setField('license_plate', event.target.value.toUpperCase())} required />
            <Input id="vehicle-capacity" type="number" min="1" label="Capacidad" value={form.capacity} onChange={(event) => setField('capacity', event.target.value)} required />
            <Select id="vehicle-driver" label="Conductor principal" value={form.driver_id} onChange={(event) => setField('driver_id', event.target.value)} options={driverOptions.map((item) => ({ value: item.id, label: `${item.name}${item.approval_status === 'PENDING' ? ' · Pendiente de aprobación' : ' · Aprobado'}` }))} placeholder={form.coordinator_id ? 'Seleccionar conductor disponible' : 'Selecciona primero un coordinador'} disabled={!form.coordinator_id} required />
            <Input id="vehicle-zone" label="Zona" value={form.zone} onChange={(event) => setField('zone', event.target.value)} required />
            <Select id="vehicle-status" label="Estado" value={form.status} onChange={(event) => setField('status', event.target.value)} options={statusOptions} required />
            <Textarea id="vehicle-observations" label="Observaciones" className="sm:col-span-2" value={form.observations} onChange={(event) => setField('observations', event.target.value)} />
          </div>
          <section className="border-t pt-5">
            <h3 className="mb-3 text-sm font-semibold text-content-primary">Documentación del vehículo</h3>
            <div className="grid gap-4 lg:grid-cols-3">
              <ImageUpload id="vehicle-authorization" label="Habilitación" required={modalMode === 'create'} value={documents.authorization_image || editing?.authorization_image} onChange={(value) => setDocuments((current) => ({ ...current, authorization_image: value }))} hint="Foto completa y legible." />
              <ImageUpload id="vehicle-green-front" label="Cédula verde · Frente" required={modalMode === 'create'} value={documents.green_card_front_image || editing?.green_card_front_image} onChange={(value) => setDocuments((current) => ({ ...current, green_card_front_image: value }))} hint="Foto completa y legible." />
              <ImageUpload id="vehicle-green-back" label="Cédula verde · Dorso" required={modalMode === 'create'} value={documents.green_card_back_image || editing?.green_card_back_image} onChange={(value) => setDocuments((current) => ({ ...current, green_card_back_image: value }))} hint="Foto completa y legible." />
            </div>
          </section>
          {!driverOptions.length && form.coordinator_id && <Alert variant="warning" title="Sin conductores disponibles">Crea primero un conductor, verifica que su licencia no esté vencida o libera uno de otro móvil activo.</Alert>}
          {driverOptions.some((item) => item.approval_status === 'PENDING') && <Alert variant="info" title="Aprobación administrativa">Puedes presentar el móvil con un conductor pendiente. El Administrador o Administrador general deberá aprobar primero al conductor y luego al móvil desde Revisión de flota.</Alert>}
        </div>
      </Modal>
    </div>
  );
}

export default VehiclesPage;
